// @flow
/* eslint-env browser */
import { combineReducers } from 'redux';
import { AsyncRequest } from 'angieslist-app-platform';
import _ from 'lodash';
import { SplitSdk } from '@splitsoftware/splitio-react';
import { getAgreementStatus } from './agreementActions';
import agreementActionKeys from './agreementActionKeys';
import userActionKeys from '../user/userActionKeys';
import { splitSdkConfig } from '../../../modules/common/splitSdkConfigSetup';

const allAgreementsSigned = response => _.every(response, v => !!v);

const dispatchPactsafeAction = (store) => {
  const storeState = store.getState();
  const userId = _.get(storeState, 'user.userInfo.response.userId');
  const previousPactsafeResponse = _.get(storeState, 'agreement.agreementStatus.response');
  const isAutomatedTesting = _.get(storeState, 'config.isAutomatedTesting', false);

  const props = store.getState();
  const client = SplitSdk(splitSdkConfig(props.config.splitClientSideApiKey)).client();
  client.on(client.Event.SDK_READY, () => {
    const treatment = client.getTreatment('pro_WebTermsAndConditions');
    if (treatment !== 'on' && !isAutomatedTesting && userId
      && (!previousPactsafeResponse || !allAgreementsSigned(previousPactsafeResponse))
    ) {
      const pactsafeAccessId = _.get(storeState, 'config.pactsafeAccessId');
      store.dispatch(getAgreementStatus(userId, pactsafeAccessId));
    }
  });
};

function createAgreementStatusReducer(
  authorized: boolean,
  enableRedirect: Promise<any>,
  resolveEnableRedirect: () => any,
  rejectEnableRedirect: () => any,
) {
  return AsyncRequest.createReducer(agreementActionKeys.GET_AGREEMENT_STATUS, {
    [userActionKeys.GET_USER_SUCCESS]: (state, action) => {
      action.addSideEffect((store) => {
        dispatchPactsafeAction(store);
      });
      return state;
    },
    [agreementActionKeys.ENABLE_LEGAL_REDIRECT]: (state, action) => {
      action.addSideEffect(() => {
        resolveEnableRedirect();
      });
      return state;
    },
    [agreementActionKeys.DISABLE_LEGAL_REDIRECT]: (state, action) => {
      action.addSideEffect(() => {
        rejectEnableRedirect();
      });
      return state;
    },
    [agreementActionKeys.GET_AGREEMENT_STATUS_SUCCESS]: (state, action) => {
      if (
        authorized
        && !allAgreementsSigned(action.payload.body)
        && process.env.IS_BROWSER
        && !window.location.pathname.startsWith('/app/legal')
      ) {
        action.addSideEffect(async () => {
          try {
            await enableRedirect;
            // redirect on timeout to avoid Redux persist issue
            // where AsyncRequest is persisted as isLoading=true
            setTimeout(() => {
              window.location.replace(`/app/legal?redirect=${encodeURIComponent(window.location.href)}`);
            }, 1);
          } catch (e) {
            // don't redirect
          }
        });
      }
      return AsyncRequest.setSuccess(state, action.payload.body);
    },
  });
}

// Wrapper function to create the agreement reducer for authorized/non-authorized pages
// Pass authorized=true for authorized pages where you want redirect behavior.
// If site legal agreements are not signed then the user will be redirected
// to /app/legal.
// You can pass the deferLegalRedirect true option to delay redirecting until
// you fire ENABLE_LEGAL_REDIRECT (or DISABLE_LEGAL_REDIRECT to cancel permanentally).
export default function (authorized: boolean, deferLegalRedirect: boolean = false): any {
  // a simple single use async guard to delay redirecting if deferLegalRedirect is true
  let resolveEnableRedirect = () => {};
  let rejectEnableRedirect = () => {};
  const enableRedirect = new Promise((resolve, reject) => {
    resolveEnableRedirect = resolve;
    rejectEnableRedirect = reject;
  });
  if (!deferLegalRedirect) {
    resolveEnableRedirect();
  }

  return combineReducers({
    agreementStatus: createAgreementStatusReducer(
      authorized,
      enableRedirect,
      resolveEnableRedirect,
      rejectEnableRedirect,
    ),
  });
}



// WEBPACK FOOTER //
// ./src/app/common/agreement/agreementReducer.js